/**
 * VolumeFlowTab — Tasks Reports Phase 0 tab.
 *
 * KPI Tiles (Created, Assigned, Completed, In Progress, Open, Backlog,
 * Net Flow, Throughput) + Status Distribution donut + Net Flow line chart.
 */
import React, { useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, UserCheck, CheckCircle2, Clock, Inbox, Layers,
  TrendingUp, Activity, ArrowUp, ArrowDown, Minus,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

// ─── KPI Tile ──────────────────────────────────────────────────────────────
const Tile = ({ icon: Icon, label, value, hint, delta, tone = 'neutral', spark, testid }) => {
  const toneCls = {
    pink:    'text-pink-700',
    emerald: 'text-emerald-700',
    amber:   'text-amber-700',
    red:     'text-red-700',
    blue:    'text-blue-700',
    neutral: 'text-neutral-800',
  }[tone] || 'text-neutral-800';
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${toneCls}`} />}
      </div>
      <div className={`text-[28px] font-semibold leading-none ${toneCls} tabular-nums`}>{value}</div>
      <div className="flex items-center justify-between mt-2">
        <div className="text-[11px] text-neutral-400">{hint}</div>
        {delta !== undefined && delta !== null && <DeltaPill v={delta} />}
      </div>
      {spark && <div className="mt-3"><Sparkline data={spark} tone={tone} /></div>}
    </div>
  );
};

const DeltaPill = ({ v }) => {
  if (v === 0) return (
    <span className="inline-flex items-center gap-1 text-[10px] text-neutral-500">
      <Minus className="w-3 h-3" /> 0%
    </span>
  );
  const up = v > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${up ? 'text-emerald-600' : 'text-red-600'}`}>
      {up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {Math.abs(v)}%
    </span>
  );
};

// ─── Sparkline (pure SVG, no recharts dep) ─────────────────────────────────
const Sparkline = ({ data, tone = 'pink' }) => {
  if (!data || data.length < 2) return <div className="h-8" />;
  const stroke = ({
    pink: '#EC4899', emerald: '#10B981', amber: '#F59E0B',
    red: '#EF4444', blue: '#3B82F6', neutral: '#737373',
  })[tone] || '#EC4899';
  const w = 100, h = 28;
  const max = Math.max(1, ...data);
  const step = w / (data.length - 1);
  const pts = data.map((v, i) => `${(i * step).toFixed(2)},${(h - (v / max) * h).toFixed(2)}`).join(' ');
  const area = `M0,${h} L${pts.split(' ').map(p => p).join(' L')} L${w},${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-8">
      <path d={area} fill={stroke} opacity="0.12" />
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.5" />
    </svg>
  );
};

// ─── Net Flow Chart ────────────────────────────────────────────────────────
const NetFlowChart = ({ series }) => {
  if (!series || series.length < 2) return <EmptyChart label="Not enough data for a flow chart" />;
  const w = 600, h = 200, pad = 28;
  const max = Math.max(1, ...series.flatMap((s) => [s.created, s.completed]));
  const step = (w - pad * 2) / (series.length - 1);
  const xy = (i, v) => [pad + i * step, h - pad - (v / max) * (h - pad * 2)];

  const path = (key) =>
    series.map((s, i) => {
      const [x, y] = xy(i, s[key]);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    }).join(' ');

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="net-flow-chart">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[13px] font-semibold text-[#1a1a2e]">Net Flow</div>
          <div className="text-[11px] text-neutral-500">Tasks created vs. completed across the window</div>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-pink-500" /> Created</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" /> Completed</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: '200px' }}>
        {/* gridlines */}
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <line key={g} x1={pad} x2={w - pad}
            y1={h - pad - g * (h - pad * 2)} y2={h - pad - g * (h - pad * 2)}
            stroke="#F5F5F5" strokeWidth="1" />
        ))}
        <line x1={pad} x2={w - pad} y1={h - pad} y2={h - pad} stroke="#E5E5E5" />
        <path d={path('created')} fill="none" stroke="#EC4899" strokeWidth="2" />
        <path d={path('completed')} fill="none" stroke="#10B981" strokeWidth="2" />
        {/* x labels (sparse) */}
        {series.map((s, i) => {
          if (i % Math.ceil(series.length / 6) !== 0) return null;
          const [x] = xy(i, 0);
          return (
            <text key={s.date} x={x} y={h - 8} fontSize="9" textAnchor="middle" fill="#737373">
              {s.date.slice(5)}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

const EmptyChart = ({ label }) => (
  <div className="bg-white border border-dashed border-neutral-200 rounded-xl p-10 text-center text-sm text-neutral-500">
    {label}
  </div>
);

// ─── Status Mix Donut ──────────────────────────────────────────────────────
const STATUS_COLORS = {
  New: '#3B82F6', 'In Progress': '#EC4899', Review: '#F59E0B',
  Revision: '#EF4444', Completed: '#10B981',
};

const StatusDonut = ({ mix }) => {
  const total = (mix || []).reduce((s, m) => s + m.count, 0);
  if (!total) return <EmptyChart label="No tasks in this window" />;
  let acc = 0;
  const r = 70, cx = 90, cy = 90, stroke = 18;
  const segments = mix.map((m) => {
    const frac = m.count / total;
    const start = acc; acc += frac;
    const a0 = start * 2 * Math.PI - Math.PI / 2;
    const a1 = acc * 2 * Math.PI - Math.PI / 2;
    const large = frac > 0.5 ? 1 : 0;
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    return {
      d: `M${x0.toFixed(2)},${y0.toFixed(2)} A${r},${r} 0 ${large} 1 ${x1.toFixed(2)},${y1.toFixed(2)}`,
      color: STATUS_COLORS[m.status] || '#737373',
      label: m.status,
      count: m.count,
      pct: Math.round(frac * 100),
    };
  });
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="status-donut">
      <div className="text-[13px] font-semibold text-[#1a1a2e] mb-2">Status distribution</div>
      <div className="flex items-center gap-6">
        <svg viewBox="0 0 180 180" className="w-[180px] h-[180px] shrink-0">
          {segments.map((s, i) => (
            <path key={i} d={s.d} stroke={s.color} strokeWidth={stroke} fill="none" />
          ))}
          <text x="90" y="86" textAnchor="middle" fontSize="22" fontWeight="600" fill="#1a1a2e">{total}</text>
          <text x="90" y="104" textAnchor="middle" fontSize="10" fill="#737373">tasks</text>
        </svg>
        <div className="flex-1 space-y-1.5">
          {segments.map((s) => (
            <div key={s.label} className="flex items-center justify-between gap-2 text-[12px]">
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: s.color }} />
                <span className="text-neutral-700">{s.label}</span>
              </div>
              <div className="tabular-nums text-neutral-500">{s.count} · {s.pct}%</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Main Tab ──────────────────────────────────────────────────────────────
const VolumeFlowTab = ({ filters }) => {
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = {
      from_date: filters.fromDate, to_date: filters.toDate,
      compare: true,
    };
    if (filters.assigneeId && filters.assigneeId !== 'all') p.assignee_id = filters.assigneeId;
    if (filters.clientId && filters.clientId !== 'all') p.client_id = filters.clientId;
    if (filters.department && filters.department !== 'all') p.department = filters.department;
    if (filters.taskType && filters.taskType !== 'all') p.task_type = filters.taskType;
    if (filters.subtype && filters.subtype !== 'all') p.subtype = filters.subtype;
    if (filters.status && filters.status !== 'all') p.status = filters.status;
    if (filters.priority && filters.priority !== 'all') p.priority = filters.priority;
    if (filters.search) p.search = filters.search;
    return p;
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/admin/reports/tasks/overview`, { headers: auth(), params }),
      axios.get(`${API}/api/admin/reports/tasks/trend`, { headers: auth(), params: { ...params, compare: undefined } }),
    ])
      .then(([o, t]) => {
        if (cancelled) return;
        setOverview(o.data);
        setTrend(t.data);
      })
      .catch((e) => !cancelled && toast.error(e.response?.data?.detail || 'Failed to load metrics'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  if (loading && !overview) return <SkeletonGrid />;

  const t = overview?.tiles || {};
  const prev = overview?.previous?.tiles;
  const series = trend?.series || [];
  const createdSpark = series.map((s) => s.created);
  const completedSpark = series.map((s) => s.completed);

  const pctDelta = (cur, p) => {
    if (p === 0) return cur > 0 ? 100 : 0;
    return Math.round(((cur - p) / Math.max(1, p)) * 100);
  };

  return (
    <div className="space-y-4" data-testid="volume-flow-tab">
      {/* Row 1: Volume tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          icon={Plus} label="Created" value={t.created ?? 0}
          hint={`in this window`} spark={createdSpark} tone="pink"
          delta={prev ? pctDelta(t.created, prev.created) : null}
          testid="tile-created"
        />
        <Tile
          icon={UserCheck} label="Assigned" value={t.assigned ?? 0}
          hint={`out of ${t.created ?? 0}`} tone="blue"
          delta={prev ? pctDelta(t.assigned, prev.assigned) : null}
          testid="tile-assigned"
        />
        <Tile
          icon={CheckCircle2} label="Completed" value={t.completed ?? 0}
          hint={`in this window`} spark={completedSpark} tone="emerald"
          delta={prev ? pctDelta(t.completed, prev.completed) : null}
          testid="tile-completed"
        />
        <Tile
          icon={Activity} label="Throughput" value={t.throughput ?? 0}
          hint={`completed · per working day`} tone="amber"
          testid="tile-throughput"
        />
      </div>

      {/* Row 2: Live / pending tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={Clock} label="In Progress" value={t.in_progress ?? 0} hint="active now" tone="pink" testid="tile-in-progress" />
        <Tile icon={Inbox} label="Open" value={t.open ?? 0} hint="awaiting start" tone="blue" testid="tile-open" />
        <Tile icon={Layers} label="Pending" value={t.pending ?? 0} hint="open + review + revision" tone="amber" testid="tile-pending" />
        <Tile
          icon={TrendingUp} label="Net Flow"
          value={(t.net_flow ?? 0) > 0 ? `+${t.net_flow}` : t.net_flow ?? 0}
          hint="created − completed" tone={t.net_flow > 0 ? 'red' : t.net_flow < 0 ? 'emerald' : 'neutral'}
          testid="tile-net-flow"
        />
      </div>

      {/* Row 3: Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2"><NetFlowChart series={series} /></div>
        <StatusDonut mix={t.status_mix} />
      </div>
    </div>
  );
};

const SkeletonGrid = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-white border border-neutral-200 rounded-xl p-4 animate-pulse">
          <div className="h-3 w-20 bg-neutral-200 rounded mb-3" />
          <div className="h-8 w-16 bg-neutral-200 rounded" />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-white border border-neutral-200 rounded-xl p-4 animate-pulse h-[96px]" />
      ))}
    </div>
    <div className="bg-white border border-neutral-200 rounded-xl p-4 animate-pulse h-[220px]" />
  </div>
);

export default VolumeFlowTab;
